/**
 * cargaSaldosBanca — orquestación de la carga progresiva de la pantalla Saldos.
 *
 * ANTES
 * `SaldosPage` disparaba `/saldos`, `/banca/saldo/grupos` y
 * `/sorteos/pendientes-ganador` dentro de un único `Promise.all` y solo
 * quitaba el indicador de carga en el `finally`. Consecuencia: aunque
 * `/saldos` respondiera en 200 ms, la tabla no aparecía hasta que terminaba
 * la más lenta de las tres.
 *
 * AHORA
 * - `/saldos` es la fuente PRINCIPAL: gobierna la tabla y el indicador.
 * - Grupos y pendientes son SECUNDARIAS: se resuelven en paralelo y su fallo
 *   nunca oculta ni borra la tabla ya pintada.
 * - Pendientes pasa por `obtenerPendientesGanador`, que coalesce esta llamada
 *   con la de `AppLayout`: una sola solicitud HTTP al entrar a la pantalla.
 *
 * La lógica vive aquí —fuera del componente— para poder probarla sin montar
 * React y sin agregar dependencias de testing nuevas.
 */

import { fetchAPI } from '../../../services/apiClient';
import {
  obtenerPendientesGanador,
  type SorteoPendienteGanador,
} from '../../../services/pendientesGanador';

export interface DependenciasCargaSaldos<TSaldo, TGrupo> {
  fetchSaldos?: () => Promise<TSaldo[]>;
  fetchGrupos?: () => Promise<TGrupo[]>;
  fetchPendientes?: () => Promise<SorteoPendienteGanador[]>;
}

export interface ManejadoresCargaSaldos<TSaldo, TGrupo> {
  /** Debe devolver false si el componente ya se desmontó. */
  vivo: () => boolean;
  /** Datos de la tabla principal. */
  onSaldos: (datos: TSaldo[]) => void;
  /** Error de la fuente PRINCIPAL únicamente. */
  onError: (mensaje: string) => void;
  /** Se llama cuando `/saldos` termina, con éxito o error. */
  onFinPrincipal: () => void;
  /** Datos de la vista por grupo (secundaria). */
  onGrupos: (datos: TGrupo[]) => void;
  /** Sorteos sin ganador (secundaria). */
  onPendientes: (datos: SorteoPendienteGanador[]) => void;
}

/**
 * Lanza las tres cargas y resuelve cuando termina la PRINCIPAL.
 *
 * Las secundarias siguen en vuelo después de que esta promesa resuelve: eso
 * es intencional, es lo que permite pintar la tabla antes.
 */
export function cargarSaldosBanca<TSaldo, TGrupo>(
  manejadores: ManejadoresCargaSaldos<TSaldo, TGrupo>,
  deps: DependenciasCargaSaldos<TSaldo, TGrupo> = {},
): Promise<void> {
  const {
    vivo, onSaldos, onError, onFinPrincipal, onGrupos, onPendientes,
  } = manejadores;

  const pedirSaldos =
    deps.fetchSaldos ?? (() => fetchAPI<TSaldo[]>('/saldos'));
  const pedirGrupos =
    deps.fetchGrupos ?? (() => fetchAPI<TGrupo[]>('/banca/saldo/grupos'));
  const pedirPendientes =
    deps.fetchPendientes ?? (() => obtenerPendientesGanador());

  // ── SECUNDARIA: vista por grupo ──
  // Un fallo aquí deja la vista por grupo vacía, nunca toca la tabla.
  void pedirGrupos()
    .then((datos) => { if (vivo()) onGrupos(datos); })
    .catch(() => { if (vivo()) onGrupos([]); });

  // ── SECUNDARIA: sorteos sin ganador ──
  void pedirPendientes()
    .then((datos) => { if (vivo()) onPendientes(datos); })
    .catch(() => { if (vivo()) onPendientes([]); });

  // ── PRINCIPAL: gobierna tabla e indicador de carga ──
  return pedirSaldos()
    .then((datos) => { if (vivo()) onSaldos(datos); })
    .catch((e: unknown) => {
      if (!vivo()) return;
      const mensaje =
        e instanceof Error ? e.message : 'Error al cargar saldos';
      onError(mensaje || 'Error al cargar saldos');
    })
    .finally(() => { if (vivo()) onFinPrincipal(); });
}
